import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  fetchAppealTypes,
  findOneAppealContribution,
  formatCurrency,
  formatSapDate,
  getDeceasedResults,
  getStatusBadgeClass,
  getStatusLabel,
  pickField,
} from "./appealContribution.service";

const NO_DATA = "-";

const AppealContributionDetails: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { referenceNumber } = useParams<{ referenceNumber: string }>();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [appealTypeMap, setAppealTypeMap] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const options = await fetchAppealTypes();
        if (cancelled) return;
        const map: Record<string, string> = {};
        options.forEach((opt) => {
          if (opt.value !== "") map[opt.value] = opt.label;
        });
        setAppealTypeMap(map);
      } catch {
        /* details can still render */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!referenceNumber?.trim()) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneAppealContribution(
          decodeURIComponent(referenceNumber)
        );
        setDetail(data);
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [referenceNumber, t]);

  const get = (...keys: string[]) => {
    const val = pickField(detail, ...keys);
    return val === undefined || val === null || val === "" ? NO_DATA : String(val);
  };

  const resolveAppealType = (value: string) => {
    if (!value || value === NO_DATA) return NO_DATA;
    return appealTypeMap[value] || value;
  };

  const rawStatus = get("RequestStatus", "requestStatus", "Status", "status");
  const statusLabel = getStatusLabel(rawStatus === NO_DATA ? "" : rawStatus, t);
  const appealType = get("TypeOfAppeal", "typeOfAppeal", "AppealType");
  const willingness = get("Wilingness", "Willingness", "willingness");
  const deceasedResults = getDeceasedResults(detail);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("appeal_contribution_details")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.appealContribution.list}>{t("contribution_on_appeal")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("display_request")}
                </li>
              </ol>
            </nav>
          </div>
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left me-1"></i>
            {t("close")}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : !detail ? (
          <div className="alert alert-light border">{NO_DATA}</div>
        ) : (
          <>
            <div className="alert alert-secondary d-flex align-items-center justify-content-between">
              <span className="fw-semibold">{t("request_status")}</span>
              <span className={getStatusBadgeClass(rawStatus)}>
                {statusLabel}
              </span>
            </div>

            <Section title={t("request_information")} icon="ti ti-briefcase">
              <Field label={t("reference_no")} value={get("ReferenceNumber", "referenceNumber")} />
              <Field
                label={t("creation_date")}
                value={formatSapDate(
                  pickField(detail, "RequestCreationDate", "created_at", "CreationDate")
                )}
              />
              <Field label={t("type_of_appeal")} value={resolveAppealType(appealType)} />
              <Field label={t("willingness")} value={willingness} />
              <Field label={t("request_status")} value={statusLabel} />
            </Section>

            <Section title={t("employee_details")} icon="ti ti-user">
              <Field label={t("emp_id")} value={get("EmpID", "empId", "PersonalNo")} />
              <Field label={t("employee_name")} value={get("EmployeeName", "employeeName")} />
              <Field label={t("department")} value={get("Department", "department")} />
              <Field
                label={t("date_of_joining")}
                value={
                  detail?.DateOfJoining ? formatSapDate(detail.DateOfJoining) : NO_DATA
                }
              />
            </Section>

            <Section title={t("contribution_details")} icon="ti ti-coin">
              <Field
                label={t("deceased_emp_no")}
                value={get("DeceasedEmployee", "deceasedEmployee")}
              />
              <Field
                label={t("deceased_emp_name")}
                value={get("DeceasedEmployeeName", "deceasedEmployeeName")}
              />
              <Field
                label={t("deceased_department")}
                value={get("DeceasedEmployeeDepartment", "deceasedEmployeeDepartment")}
              />
              <Field
                label={t("deceased_designation")}
                value={get("DeceasedEmployeeDesignation", "deceasedEmployeeDesignation")}
              />
              <Field
                label={t("emp_contribution")}
                value={formatCurrency(
                  pickField(
                    detail,
                    "DeceasedEmpContributionAmount",
                    "deceasedEmpContributionAmount"
                  )
                )}
              />
              <Field
                label={t("total_contribution")}
                value={formatCurrency(
                  pickField(detail, "TotalAmountContribute", "totalAmountContribute")
                )}
              />
            </Section>

            {appealType === "0" &&
              String(willingness).toUpperCase() === "YES" &&
              deceasedResults.length > 0 && (
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-white">
                    <h5 className="mb-0 fw-bold">
                      <i className="ti ti-users me-2 text-danger"></i>
                      {t("deceased_employees")}
                    </h5>
                  </div>
                  <div className="card-body">
                    {deceasedResults.map((dec: any, idx: number) => (
                      <div
                        key={dec?.SequenceNumber ?? idx}
                        className={`pb-3 mb-3 ${
                          idx < deceasedResults.length - 1 ? "border-bottom" : ""
                        }`}
                      >
                        <div className="d-flex gap-2 mb-2 flex-wrap">
                          <span className="fw-bold text-danger">
                            {dec?.DeceasedEmployeeNumber ?? dec?.EmpNo ?? NO_DATA}
                          </span>
                          <span className="fw-semibold">
                            {dec?.DeceasedEmployeeName ?? dec?.EmpName ?? NO_DATA}
                          </span>
                        </div>
                        <Field label={t("designation")} value={dec?.Designation ?? NO_DATA} />
                        <Field label={t("department")} value={dec?.Department ?? NO_DATA} />
                        <Field
                          label={t("start_date")}
                          value={formatSapDate(dec?.AppealStartDate ?? dec?.StartDate)}
                        />
                        <Field
                          label={t("end_date")}
                          value={formatSapDate(dec?.AppealEndDate ?? dec?.EndDate)}
                        />
                        <Field label={t("amount")} value={formatCurrency(dec?.Amount)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

            <div className="alert alert-success d-flex align-items-start">
              <i className="ti ti-info-circle me-2 mt-1"></i>
              <span>{t("readonly_notice")}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">
        <i className={`${icon} me-2 text-danger`}></i>
        {title}
      </h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="row mb-2">
    <div className="col-md-4 text-muted fw-semibold">{label}</div>
    <div className="col-md-8 text-dark">{value ?? NO_DATA}</div>
  </div>
);

export default AppealContributionDetails;
